'use strict';

/**
 * AvatarHelper
 * ------------
 * Returns a self-contained avatar element (string) for a user: their photo thumb
 * if they have one, otherwise a colored circle with their initials. Styles are
 * inline so it drops into any template or innerHTML without needing CSS injected
 * first (topbar, user settings, and the team list all reuse it).
 *
 *   AvatarHelper.avatarHTML({ IDUser, PhotoHash, DisplayName, Email, Color }, { Size: 28 })
 *
 * The initials background uses the user's chosen Color when it is a valid hex;
 * otherwise a stable color is derived from the name so each person is consistent.
 */

function _esc(pStr)
{
	return String(pStr == null ? '' : pStr)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function _isHex(pColor)
{
	return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(pColor || ''));
}

function _luminance(pHex)
{
	let tmpHex = String(pHex).replace('#', '');
	if (tmpHex.length === 3) { tmpHex = tmpHex.split('').map((pCh) => pCh + pCh).join(''); }
	let tmpR = parseInt(tmpHex.slice(0, 2), 16);
	let tmpG = parseInt(tmpHex.slice(2, 4), 16);
	let tmpB = parseInt(tmpHex.slice(4, 6), 16);
	return (0.299 * tmpR + 0.587 * tmpG + 0.114 * tmpB) / 255;
}

// Stable mid-tone color derived from a string (used when no Color is set).
function _derivedColor(pStr)
{
	let tmpStr = String(pStr || 'x');
	let tmpHash = 0;
	for (let i = 0; i < tmpStr.length; i++) { tmpHash = (tmpHash * 31 + tmpStr.charCodeAt(i)) >>> 0; }
	return 'hsl(' + (tmpHash % 360) + ', 52%, 45%)';
}

function initials(pName)
{
	let tmpName = String(pName || '').trim();
	if (!tmpName) { return '?'; }
	// For an email-looking value, use the local part before the @.
	if (tmpName.indexOf('@') > 0 && tmpName.indexOf(' ') < 0) { tmpName = tmpName.split('@')[0]; }
	let tmpParts = tmpName.replace(/[^a-zA-Z0-9 ]/g, ' ').trim().split(/\s+/).filter(Boolean);
	if (!tmpParts.length) { return '?'; }
	if (tmpParts.length === 1) { return tmpParts[0].slice(0, 2).toUpperCase(); }
	return (tmpParts[0].charAt(0) + tmpParts[tmpParts.length - 1].charAt(0)).toUpperCase();
}

function backgroundFor(pColor, pName)
{
	return _isHex(pColor) ? pColor : _derivedColor(pName);
}

function onColorFor(pColor)
{
	if (_isHex(pColor)) { return _luminance(pColor) > 0.6 ? '#1d2230' : '#ffffff'; }
	return '#ffffff';
}

function avatarHTML(pUser, pOptions)
{
	let tmpUser = pUser || {};
	let tmpOpts = pOptions || {};
	let tmpSize = tmpOpts.Size || 28;
	let tmpName = tmpUser.DisplayName || tmpUser.Email || tmpUser.LoginID || 'You';
	let tmpRadius = tmpOpts.Square ? '6px' : '50%';
	let tmpBase = 'display:inline-flex;flex:0 0 auto;box-sizing:border-box;width:' + tmpSize + 'px;height:' + tmpSize + 'px;border-radius:' + tmpRadius + ';vertical-align:middle;';

	if (tmpUser.PhotoHash && tmpUser.IDUser)
	{
		let tmpURL = '/1.0/Profile/Photo/' + encodeURIComponent(tmpUser.IDUser) + '?thumb=1&v=' + encodeURIComponent(tmpUser.PhotoHash);
		return '<img class="cst-avatar" alt="' + _esc(tmpName) + '" src="' + tmpURL + '" style="' + tmpBase + 'object-fit:cover;border:0;background:rgba(0,0,0,0.06);" />';
	}

	let tmpBg = backgroundFor(tmpUser.Color, tmpName);
	let tmpFg = onColorFor(tmpUser.Color);
	let tmpFont = Math.max(9, Math.round(tmpSize * 0.42));
	return '<span class="cst-avatar cst-avatar-initials" aria-label="' + _esc(tmpName) + '" style="' + tmpBase
		+ 'align-items:center;justify-content:center;background:' + tmpBg + ';color:' + tmpFg + ';font-size:' + tmpFont + 'px;font-weight:600;line-height:1;letter-spacing:0.02em;">'
		+ _esc(initials(tmpName)) + '</span>';
}

module.exports =
{
	avatarHTML: avatarHTML,
	initials: initials,
	backgroundFor: backgroundFor,
	onColorFor: onColorFor
};
